import React, { useEffect, useRef, useState } from 'react';
import { useT, LANGS } from '../lib/i18n';

/**
 * Language selector — a quiet, two-letter trigger that opens a small list.
 *
 * Style: copper-on-stone, ultra-low-contrast, no gimmicks. The button shows the
 * current language as a 2-letter code; the popover lists the native names.
 *
 * The popover auto-flips: opens upward when there isn't enough room below.
 */
export default function LangSwitch({ className = '', testId = 'lang-switch' }) {
  const { lang, setLang, dir } = useT();
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  const current = LANGS.find(l => l.code === lang) || LANGS[0];

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // Approx popover height: 14 items × 28px row + 16px vertical padding.
      const popoverHeight = 14 * 28 + 16;
      setOpenUp(spaceBelow < popoverHeight);
    }
    setOpen(o => !o);
  };

  const popoverEdge = dir === 'rtl' ? 'left-0' : 'right-0';
  const popoverVerticalEdge = openUp ? 'bottom-full mb-3' : 'top-full mt-3';

  return (
    <div ref={wrapRef} className={`relative inline-block ${className}`} data-testid={testId}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 bg-transparent border-0 p-0 cursor-pointer font-pressura"
        style={{ fontWeight: 700 }}
        data-testid="lang-switch-trigger"
      >
        {current.short || current.code.toUpperCase()}
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Language"
          className={`absolute ${popoverEdge} ${popoverVerticalEdge} min-w-[10rem] py-2 rounded-md bg-white shadow-[0_8px_28px_-12px_rgba(42,39,36,0.25)] ring-1 ring-[#2A2724]/10 z-50`}
          style={{ direction: dir }}
          data-testid="lang-switch-list"
        >
          {LANGS.map(l => (
            <li key={l.code} role="option" aria-selected={l.code === lang}>
              <button
                type="button"
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full px-4 py-1.5 text-sm bg-transparent border-0 cursor-pointer transition-opacity duration-200 ${dir === 'rtl' ? 'text-right' : 'text-left'} ${l.code === lang ? 'text-[#B85228] opacity-100' : 'text-[#2A2724] opacity-70 hover:opacity-100'}`}
                style={{ fontWeight: l.code === lang ? 600 : 500 }}
                data-testid={`lang-switch-option-${l.code}`}
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
